import { useState } from 'react';
import '../../css/GetHelpContent.css';

const MODULE_OPTIONS = [
    'Equipment',
    'Forms',
    'Inspections',
    'Issues',
    'Preventive Maintenance',
    'Tire Management',
    'Safety & Training',
    'Cost Tracking',
    'Reports',
    'Resources',
    'Users',
    'Regions',
    'Settings',
    'Other',
];

interface ReportBugModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: { description: string; expected: string; module: string }) => void;
}

export const ReportBugModal = ({ isOpen, onClose, onSubmit }: ReportBugModalProps) => {
    const [description, setDescription] = useState('');
    const [expected, setExpected] = useState('');
    const [module, setModule] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleClose = () => {
        setDescription('');
        setExpected('');
        setModule('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!description.trim() || !expected.trim()) return;
        setIsSubmitting(true);
        await onSubmit({ description: description.trim(), expected: expected.trim(), module: module || 'Not specified' });
        setIsSubmitting(false);
        setDescription('');
        setExpected('');
        setModule('');
    };

    return (
        <div className="gh-modal-overlay" onClick={handleClose}>
            <div className="gh-modal" onClick={(e) => e.stopPropagation()}>
                <div className="gh-modal-header">
                    <h2 className="gh-modal-title">Report a Bug</h2>
                    <button className="gh-modal-close" onClick={handleClose} aria-label="Close">×</button>
                </div>
                <div className="gh-modal-body">
                    <p className="gh-modal-hint">
                        Help us fix it faster by including as much detail as you can.
                    </p>

                    <div className="gh-field">
                        <label className="gh-field-label">
                            What were you trying to do? <span className="gh-required">*</span>
                        </label>
                        <input
                            type="text"
                            className="gh-field-input"
                            placeholder="e.g. Save a new inspection report"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>

                    <div className="gh-field">
                        <label className="gh-field-label">
                            What happened instead? <span className="gh-required">*</span>
                        </label>
                        <textarea
                            className="gh-field-textarea"
                            placeholder="Describe what you saw or what went wrong"
                            value={expected}
                            onChange={(e) => setExpected(e.target.value)}
                        />
                    </div>

                    <div className="gh-field">
                        <label className="gh-field-label">Which module?</label>
                        <select
                            className="gh-field-select"
                            value={module}
                            onChange={(e) => setModule(e.target.value)}
                        >
                            <option value="">Select a module...</option>
                            {MODULE_OPTIONS.map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </div>

                    <div className="gh-field">
                        <label className="gh-field-label">Attach Screenshot (optional)</label>
                        <div className="gh-upload-zone">
                            <div className="gh-upload-zone-icon">📎</div>
                            <p className="gh-upload-zone-text">Click to upload or drag and drop</p>
                        </div>
                    </div>
                </div>
                <div className="gh-modal-footer">
                    <button className="gh-btn gh-btn--cancel" onClick={handleClose}>Cancel</button>
                    <button
                        className="gh-btn gh-btn--submit-bug"
                        disabled={!description.trim() || !expected.trim() || isSubmitting}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? 'Sending...' : 'Send Bug Report'}
                    </button>
                </div>
            </div>
        </div>
    );
};
